import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  CircularProgress,
  Fade,
  Paper,
  Avatar,
  IconButton,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  FaTools as ConstructionIcon,
  FaSync as RefreshIcon,
  FaEnvelope as EmailIcon,
  FaPhone as PhoneIcon,
  FaTwitter as TwitterIcon,
  FaFacebook as FacebookIcon,
  FaInstagram as InstagramIcon
} from 'react-icons/fa';
import { getMaintenanceConfig } from '../utils/maintenance';
import logo from '../logo.svg';

const MaintenancePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const config = getMaintenanceConfig();

  useEffect(() => {
    // Simulate loading and start fade-in animation
    const timer = setTimeout(() => {
      setLoading(false);
      setTimeout(() => setFadeIn(true), 100);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Update document title for SEO
    document.title = 'Website Under Maintenance - Chef Republic';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Chef Republic website is currently under maintenance. We\'ll be back soon with improved services.');
    }

    return () => {
      // Reset title when component unmounts
      document.title = 'Chef Republic';
    };
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress 
          size={60} 
          thickness={4}
          sx={{ color: 'white' }}
        />
      </Box>
    );
  }

  return (
    <Fade in={fadeIn} timeout={1000}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FFEEEB 0%, #FF5E41 100%)',
          p: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            animation: 'float 6s ease-in-out infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '15%',
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.05)',
            animation: 'float 8s ease-in-out infinite reverse',
          }}
        />

        <Container maxWidth="md">
          <Paper
            elevation={24}
            sx={{
              p: isMobile ? 4 : 6,
              textAlign: 'center',
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            {/* Chef Republic Logo */}
            <Box
              component="img"
              src={logo}
              alt="Chef Republic Logo"
              sx={{
                height: isMobile ? 60 : 80,
                mx: 'auto',
                mb: 2,
                display: 'block',
              }}
            />

            {/* Maintenance Icon */}
            <Avatar
              sx={{
                width: isMobile ? 60 : 80,
                height: isMobile ? 60 : 80,
                bgcolor: '#FF5E41',
                mx: 'auto',
                mb: 3,
                boxShadow: 3,
              }}
            >
              <ConstructionIcon 
                sx={{ 
                  fontSize: isMobile ? 30 : 40,
                  color: 'white'
                }} 
              />
            </Avatar>

            {/* Main Heading */}
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
              }}
            >
              Website Under Maintenance
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              We are currently under maintainance. Please use the website after 6 AM EST
            </Typography>

            {/* Estimated Recovery Time */}
            <Typography
              variant="h6"
              color="#FF5E41"
              sx={{ mb: 4, fontWeight: 600 }}
            >
              Estimated recovery time: {config.estimatedRecovery}
            </Typography>

            {/* Contact Information */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Need immediate assistance?
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Link
                  href={`mailto:${config.contactEmail}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#FF5E41',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <EmailIcon />
                  <Typography variant="body2">{config.contactEmail}</Typography>
                </Link>
              </Box>
            </Box>

            {/* Social Links */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                Follow us for updates
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <IconButton
                  href={config.socialLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#FF5E41',
                    color: 'white',
                    '&:hover': { bgcolor: '#E04C39' }
                  }}
                >
                  <TwitterIcon />
                </IconButton>
                <IconButton
                  href={config.socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#FF5E41',
                    color: 'white',
                    '&:hover': { bgcolor: '#E04C39' }
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  href={config.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    bgcolor: '#FF5E41',
                    color: 'white',
                    '&:hover': { bgcolor: '#E04C39' }
                  }}
                >
                  <InstagramIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Refresh Button */}
            <IconButton
              onClick={handleRefresh}
              sx={{
                bgcolor: '#125066',
                color: 'white',
                p: 2,
                '&:hover': { 
                  bgcolor: '#0F3F52',
                  transform: 'rotate(180deg)',
                  transition: 'transform 0.3s ease'
                }
              }}
            >
              <RefreshIcon />
            </IconButton>

            <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
              Click to check if maintenance is complete
            </Typography>
          </Paper>
        </Container>

        {/* CSS for floating animation */}
        <style jsx>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
        `}</style>
      </Box>
    </Fade>
  );
};

export default MaintenancePage;
